import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InputRef } from 'antd';
import cc from 'classcat';
import { DEBOUNCE_DELAY } from 'utils/constants/app/magic-numbers-app';
import { GlobalIcon } from 'components/GlobalIcon';
import { Input } from 'newcomponents/UI/Input';
import { useDebounce } from 'hooks/useDebounce';

type TMarketsSearchProps = {
  setSearchValue: (value: string | null) => void;
};

const SEARCH_CHANGE_ANIMATION_DELAY = 100;

export const MarketsSearch: FC<TMarketsSearchProps> = ({ setSearchValue }) => {
  const { t } = useTranslation();
  const inputRef = useRef<InputRef | null>(null);
  const [search, setSearch] = useState<string | null>(null);
  const [debounceSearchOpen, setDebounceSearchOpen] = useState(false);
  const [isMarketsSearchOpened, setIsMarketsSearchOpened] = useState(false);

  useEffect(() => {
    if (isMarketsSearchOpened) {
      setDebounceSearchOpen(true);
      setSearchValue('');
    } else {
      setTimeout(() => {
        setDebounceSearchOpen(false);
      }, SEARCH_CHANGE_ANIMATION_DELAY);
      setSearch(null);
      setSearchValue(null);
    }
  }, [isMarketsSearchOpened]);

  useEffect(() => {
    if (debounceSearchOpen && inputRef.current) {
      inputRef.current?.focus();
    }
  }, [debounceSearchOpen]);

  useDebounce(
    () => {
      setSearchValue(search);
    },
    DEBOUNCE_DELAY._300,
    [search]
  );

  return (
    <>
      <div
        className={cc([
          'betsMenu__menu-item betsMenu__menu-item__search',
          {
            'betsMenu__menu-item__search--active': isMarketsSearchOpened
          }
        ])}
        onClick={() => setIsMarketsSearchOpened(open => !open)}
      >
        <GlobalIcon size={16} name="search" lib="generic" theme="default" />
      </div>

      {debounceSearchOpen && (
        <div
          className={cc([
            'betsMenu__menu-item betsMenu__menu-item__search betsMenu__menu-item__search--open',
            {
              'betsMenu__menu-item__search--openAnimation':
                isMarketsSearchOpened
            }
          ])}
          onClick={() => setIsMarketsSearchOpened(open => !open)}
        >
          <GlobalIcon size={12} name="close" lib="generic" theme="default" />

          <div className="betsMenu__search">
            <Input
              ref={inputRef}
              onChange={e => setSearch(e.target.value)}
              value={search ?? ''}
              onClick={e => e.stopPropagation()}
              bordered={false}
              placeholder={t('sportsbook.searchMarketsPlaceholder')}
            />
          </div>
        </div>
      )}
    </>
  );
};
